import { useState, useEffect } from "react";
import { fetchAnalyticsOverviewData, type PromptTrackingData, type AIInsight } from "@/app/services/analytics/analytics-overview-api";
import { useAnalyticsRefreshTrigger } from "./useAnalyticsRefreshTrigger";

interface UseAnalyticsOverviewDataResult {
  promptTracking: PromptTrackingData | null;
  aiInsights: Omit<AIInsight, "icon">[] | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * antennaData is the BrandMonitoringResponse from the most recent monitoring
 * run, passed down from App.tsx.  When the DB seed returns 0 rows we use it
 * to seed a snapshot directly so the user never sees the empty-state error.
 */
export function useAnalyticsOverviewData(antennaData?: any): UseAnalyticsOverviewDataResult {
  const [promptTracking, setPromptTracking] = useState<PromptTrackingData | null>(null);
  const [aiInsights, setAIInsights]         = useState<Omit<AIInsight, "icon">[] | null>(null);
  const [isLoading, setIsLoading]           = useState(true);
  const [error, setError]                   = useState<Error | null>(null);
  const [manual, setManual]                 = useState(0);
  const refreshKey                          = useAnalyticsRefreshTrigger();

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchAnalyticsOverviewData(antennaData)
      .then((result) => {
        if (!cancelled) {
          setPromptTracking(result.promptTracking);
          setAIInsights(result.aiInsights);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setIsLoading(false);
        }
      });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, manual]);
  // Note: we intentionally omit antennaData from deps — it's only used as a
  // fallback on first load, not as a reactive trigger.

  return {
    promptTracking,
    aiInsights,
    isLoading,
    error,
    refetch: () => setManual((n) => n + 1),
  };
}